import { useMemo } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getFeeConfigVersionLabel, groupFeeConfigsByLogicalFeeId } from "../../fee-config.const";
import { useFeeConfigs } from "../../hooks/use-fee-configs";
import { FeeConfigCard } from "./fee-config-card";
import { FeeConfigEditorModal } from "./fee-config-editor-modal";

type FeeConfigsModalProps = {
  onClose: () => void;
};

export const FeeConfigsModal = ({ onClose }: FeeConfigsModalProps) => {
  const {
    feeConfigs,
    feeConfigsLoading,
    editorState,
    setEditorState,
    submitEditor,
    handleCreateVersion,
    configToPublish,
    setConfigToPublish,
    confirmPublish,
    isPublishing,
    propagateTarget,
    setPropagateTarget,
    confirmPropagate,
    isPropagating,
    configToDisable,
    setConfigToDisable,
    confirmDisable,
    isDisabling,
    configToDelete,
    setConfigToDelete,
    confirmDelete,
    isDeleting,
    countRatesOnOlderVersions,
    findEnabledSibling,
  } = useFeeConfigs();

  const groups = useMemo(() => groupFeeConfigsByLogicalFeeId(feeConfigs), [feeConfigs]);

  const enabledSiblingOfPublished = configToPublish ? findEnabledSibling(configToPublish) : undefined;
  const propagatableCount = propagateTarget ? countRatesOnOlderVersions(propagateTarget) : 0;

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-2xl">
          <div className="px-6 pt-6 pb-4 border-b border-border">
            <DialogHeader>
              <DialogTitle>Commissions</DialogTitle>
              <DialogDescription>
                La commission s'ajoute au prix du stationnement. Chaque tarification référence une version précise d'une
                commission ; sans commission associée, aucun montant n'est ajouté.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-6 space-y-3">
            {feeConfigsLoading ? (
              <div className="text-sm text-muted-foreground text-center py-8">Chargement...</div>
            ) : groups.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 text-center text-xs text-muted-foreground">
                Aucune commission. Créez la première avec le bouton ci-dessous.
              </div>
            ) : (
              groups.map((group) => (
                <FeeConfigCard
                  key={group.logicalFeeId}
                  group={group}
                  ratesOnOlderVersions={countRatesOnOlderVersions}
                  onView={(config) => setEditorState({ mode: "view", config })}
                  onEdit={(config) => setEditorState({ mode: "edit", config })}
                  onPublish={setConfigToPublish}
                  onDisable={setConfigToDisable}
                  onDelete={setConfigToDelete}
                  onCreateVersion={handleCreateVersion}
                  onPropagate={setPropagateTarget}
                />
              ))
            )}
          </div>

          <div className="px-6 py-4 border-t border-border">
            <DialogFooter className="sm:justify-between">
              <Button variant="secondary" onClick={onClose} type="button">
                Fermer
              </Button>
              <Button type="button" onClick={() => setEditorState({ mode: "create" })}>
                Nouvelle commission
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {editorState && (
        <FeeConfigEditorModal state={editorState} onClose={() => setEditorState(null)} onSubmit={submitEditor} />
      )}

      <AlertDialog open={configToPublish !== null} onOpenChange={(open) => !open && setConfigToPublish(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publier « {configToPublish?.name} »</AlertDialogTitle>
            <AlertDialogDescription>
              Une fois publiée, cette version ne sera plus modifiable et pourra être référencée par les tarifications.
              {enabledSiblingOfPublished &&
                ` La version ${getFeeConfigVersionLabel(enabledSiblingOfPublished)} actuellement active sera désactivée.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPublish} disabled={isPublishing}>
              Publier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={propagateTarget !== null} onOpenChange={(open) => !open && setPropagateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Propager la nouvelle version</AlertDialogTitle>
            <AlertDialogDescription>
              {propagatableCount} tarification{propagatableCount > 1 ? "s" : ""} active
              {propagatableCount > 1 ? "s" : ""} utilise{propagatableCount > 1 ? "nt" : ""} d'anciennes versions de «{" "}
              {propagateTarget?.name} ». Créer des brouillons de tarification re-ciblés vers la version{" "}
              {propagateTarget ? getFeeConfigVersionLabel(propagateTarget) : ""} ? Ils devront ensuite être publiés
              depuis le panneau Tarifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Plus tard</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPropagate} disabled={isPropagating}>
              Créer les brouillons
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={configToDisable !== null} onOpenChange={(open) => !open && setConfigToDisable(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Désactiver « {configToDisable?.name} »</AlertDialogTitle>
            <AlertDialogDescription>
              Les tarifications qui référencent cette version continueront de l'appliquer, mais elle ne pourra plus être
              sélectionnée pour de nouvelles tarifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisable} disabled={isDisabling}>
              Désactiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={configToDelete !== null} onOpenChange={(open) => !open && setConfigToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le brouillon « {configToDelete?.name} »</AlertDialogTitle>
            <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer ce brouillon ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={isDeleting}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
